import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, d } from "@/api";
import { Badge, MODE_LABELS, DIRECTIONS, DIRECTION_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  customer_id: "", direction: "export", mode: "kara", equipment: "20DC",
  origin: "", destination: "", cargo_desc: "",
  weight_kg: 0, volume_m3: 0, packages: 0, incoterm: "EXW", ready_date: "",
  description: "", notes: "",
};

export const EQUIPMENTS = ["20DC", "40HQ", "45HCPW", "40OT", "40FR", "Parsiyel", "Full", "Havayolu"];

export default function Requests() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      customer_id: r.customer_id || "", direction: r.direction || "export", mode: r.mode || "kara", equipment: r.equipment || "20DC",
      origin: r.origin || "", destination: r.destination || "", cargo_desc: r.cargo_desc || "",
      weight_kg: r.weight_kg || 0, volume_m3: r.volume_m3 || 0, packages: r.packages || 0,
      incoterm: r.incoterm || "EXW", ready_date: (r.ready_date || "").slice(0, 10),
      description: r.description || "", notes: r.notes || "",
    });
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const load = () => api.get("/requests").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/parties", { params: { type: "musteri" } }).then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        weight_kg: Number(form.weight_kg), volume_m3: Number(form.volume_m3),
        packages: Number(form.packages), ready_date: form.ready_date || null,
      };
      if (editId) {
        await api.put(`/requests/${editId}`, payload);
        toast.success("Talep güncellendi");
      } else {
        await api.post("/requests", payload);
        toast.success("Talep oluşturuldu");
      }
      setForm(EMPTY);
      setEditId(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  const remove = async (r) => {
    if (!window.confirm(`${r.no} numaralı talep, bağlı tedarikçi ve satış teklifleriyle birlikte kalıcı olarak silinecek. Devam edilsin mi?`)) return;
    try {
      await api.delete(`/requests/${r.id}`);
      toast.success("Talep ve bağlı teklifler silindi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="requests-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Satış</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Talepler</h1>
        </div>
        {can("request:write") && (
          <button className="brut-btn" onClick={() => setOpen(!open)} data-testid="new-request-button">
            <Plus className="h-4 w-4" /> Yeni Talep
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={save} className="brut animate-fade-up p-4 sm:p-6" data-testid="request-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="overline">Müşteri</label>
              <select required className="brut-input mt-1.5" value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="request-customer-select">
                <option value="">Seçiniz</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">İşlem Tipi</label>
              <select className="brut-input mt-1.5" value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="request-direction-select">
                {DIRECTIONS.map((x) => <option key={x} value={x}>{DIRECTION_LABELS[x]}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Taşıma Modu</label>
              <select className="brut-input mt-1.5" value={form.mode}
                onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="request-mode-select">
                <option value="kara">Kara</option><option value="deniz">Deniz</option><option value="hava">Hava</option>
              </select>
            </div>
            <div>
              <label className="overline">Taşıma Birimi</label>
              <select className="brut-input mt-1.5" value={form.equipment}
                onChange={(e) => setForm({ ...form, equipment: e.target.value })} data-testid="request-equipment-select">
                {EQUIPMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Incoterm</label>
              <input className="brut-input mt-1.5" value={form.incoterm}
                onChange={(e) => setForm({ ...form, incoterm: e.target.value })} data-testid="request-incoterm-input" />
            </div>
            <div>
              <label className="overline">Çıkış</label>
              <input required className="brut-input mt-1.5" value={form.origin}
                onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="request-origin-input" />
            </div>
            <div>
              <label className="overline">Varış</label>
              <input required className="brut-input mt-1.5" value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="request-destination-input" />
            </div>
            <div>
              <label className="overline">Hazır Olma Tarihi</label>
              <input type="date" className="brut-input mt-1.5" value={form.ready_date}
                onChange={(e) => setForm({ ...form, ready_date: e.target.value })} data-testid="request-readydate-input" />
            </div>
            <div>
              <label className="overline">Yük Açıklaması</label>
              <input required className="brut-input mt-1.5" value={form.cargo_desc}
                onChange={(e) => setForm({ ...form, cargo_desc: e.target.value })} data-testid="request-cargo-input" />
            </div>
            <div>
              <label className="overline">Ağırlık (kg)</label>
              <input type="number" step="0.01" className="brut-input mt-1.5" value={form.weight_kg}
                onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="request-weight-input" />
            </div>
            <div>
              <label className="overline">Hacim (m³)</label>
              <input type="number" step="0.01" className="brut-input mt-1.5" value={form.volume_m3}
                onChange={(e) => setForm({ ...form, volume_m3: e.target.value })} data-testid="request-volume-input" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="overline">Açıklama</label>
              <textarea rows={3} className="brut-input mt-1.5" placeholder="Talebe ilişkin açıklama..."
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="request-description-input" />
            </div>
            <div>
              <label className="overline">Not</label>
              <textarea rows={3} className="brut-input mt-1.5" placeholder="İç ekip notu..."
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="request-notes-input" />
            </div>
          </div>
          <button type="submit" disabled={busy} className="brut-btn mt-5" data-testid="request-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {editId ? "Değişiklikleri Kaydet" : "Talebi Kaydet"}
          </button>
          {editId && (
            <button type="button" className="brut-btn-ghost ml-3 mt-5" data-testid="request-cancel-edit"
              onClick={() => { setEditId(null); setForm(EMPTY); setOpen(false); }}>Vazgeç</button>
          )}
        </form>
      )}

      <div className="brut overflow-x-auto" data-testid="requests-table">
        <table className="dense w-full">
          <thead>
            <tr><th>Talep No</th><th>Müşteri</th><th>Rota</th><th>Mod</th><th>Taşıma Birimi</th><th>Yük</th><th>Hazır</th><th>Durum</th><th className="!text-right">İşlem</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`request-row-${r.id}`}>
                <td>
                  <Link to={`/talepler/${r.id}`} className="font-mono font-semibold text-klein hover:underline"
                    data-testid={`request-link-${r.id}`}>{r.no}</Link>
                </td>
                <td>{r.customer_name}</td>
                <td className="text-xs">{r.origin} → {r.destination}</td>
                <td>{MODE_LABELS[r.mode]}</td>
                <td className="font-mono text-xs">{r.equipment || "-"}</td>
                <td className="tnum text-xs">{r.weight_kg} kg</td>
                <td className="text-xs">{d(r.ready_date)}</td>
                <td><Badge value={r.status} /></td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    {can("request:write") && (
                      <button onClick={() => startEdit(r)} className="text-zinc-400 hover:text-klein"
                        data-testid={`edit-request-${r.id}`} title="Düzenle">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {can("*") && (
                      <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600"
                        data-testid={`delete-request-${r.id}`} title="Kalıcı olarak sil">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="py-10 text-center text-zinc-500" data-testid="requests-empty-state">
                {loading ? "Yükleniyor..." : "Henüz talep yok."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
